/**
 * Service layer — a feeder's dates and what came back (docs/OPERATING_MODEL.md, build step 3).
 * Talks to /api/feeders only.
 */

async function authToken() {
  if (typeof window === 'undefined') return null
  for (let i = 0; i < 20; i++) {
    if (typeof window.__clerkGetToken === 'function') {
      try { const t = await window.__clerkGetToken(); if (t) return t } catch { /* not ready */ }
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return null
}
async function post(body) {
  const token = await authToken()
  const res = await fetch('/api/feeders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || ('Request failed: ' + res.status))
  return json
}

/** { feeder: null } for a non-feeder; else { feeder, isAgencyReview, posted, status, suggestedAskedOn }. */
export function getFeeder(milestoneId) { return post({ action: 'forMilestone', milestoneId }) }
export function setFeederDates(milestoneId, askedOn, expectedOn, memberName, expectedSource) {
  return post({ action: 'setStatus', milestoneId, askedOn, expectedOn, memberName, expectedSource })
}
/** Feeders right after this milestone that posting it will send: [{ milestoneId, name, subtitle, owedBy, coordinator }]. */
export function getFeedersWaitingOn(milestoneId) { return post({ action: 'waitingOn', milestoneId }).then(r => r.feeders || []) }
/** Posting sent them: marks each sent on `sentOn` with its expected date and where that date came from. */
export function markFeedersSent(milestoneId, sentOn, feeders, memberName) {
  return post({ action: 'markSent', milestoneId, sentOn, feeders, memberName })
}

/** Plain words per who owes it, shared by the feeder node and the post window. */
export const FEEDER_HANDOFF = {
  agency:     { party: 'the City',       done: 'Submitted to the City',         verb: 'submitted', pending: 'NOT YET SUBMITTED TO THE CITY',         mark: 'Mark it submitted…',            postsAs: 'Posting this submits it to the City' },
  client:     { party: 'the client',     done: 'Sent to the client',            verb: 'sent',      pending: 'NOT YET SENT TO THE CLIENT',            mark: 'Mark it sent to the client…',   postsAs: 'Posting this sends it to the client' },
  consultant: { party: 'the consultant', done: 'Requested from the consultant', verb: 'requested', pending: 'NOT YET REQUESTED FROM THE CONSULTANT', mark: 'Mark it requested…',            postsAs: 'Posting this requests it from the consultant' },
  gc:         { party: 'the GC',         done: 'Requested from the GC',         verb: 'requested', pending: 'NOT YET REQUESTED FROM THE GC',         mark: 'Mark it requested…',            postsAs: 'Posting this requests it from the GC' },
}
/** "The City estimates" / "our estimate" wording for an expected date. */
export function expectedSourceLabel(owedBy, source) {
  const party = (FEEDER_HANDOFF[owedBy] || {}).party || 'they'
  if (source === 'them') return party.charAt(0).toUpperCase() + party.slice(1) + "'s estimate"
  if (source === 'estimate') return 'our estimate'
  return ''
}
export function previewNextRound(milestoneId) { return post({ action: 'previewNextRound', milestoneId }) }
export function recordFeederCameBack(milestoneId, payload) { return post({ action: 'intake', milestoneId, ...payload }) }
export function listGameChangerFlags() { return post({ action: 'flags' }) }
/** The Arrivals board: { out, unrecorded, unlinked, asOf }. Pass a member id for one chaser's list. */
export function getArrivals(chaserMemberId) { return post({ action: 'arrivals', chaserMemberId: chaserMemberId || undefined }) }
export function acknowledgeGameChanger(milestoneId, memberName) { return post({ action: 'acknowledge', milestoneId, memberName }) }
/** Build step 5: every feeder on a project, for the Gantt — { byMilestone: { [id]: { owedBy, state:
 *  'typical'|'out'|'late'|'back', askedOn, expectedOn, expectedSource, arrivedOn, daysLate, typicalWorkdays } } }. */
export function getProjectFeeders(projectId) { return post({ action: 'forProject', projectId }) }
/** Tower: re-time feeder bars (all projects, or one). dryRun lists what would move. */
export function applyFeederSchedule({ projectId, dryRun } = {}) { return post({ action: 'applySchedule', projectId, dryRun: !!dryRun }) }

/** Whole days from today to a YYYY-MM-DD date (negative = past). */
export function daysFromToday(ymd) {
  if (!ymd) return null
  const t = new Date(); t.setHours(0, 0, 0, 0)
  return Math.round((new Date(ymd + 'T00:00:00') - t) / 86400000)
}
export function daysOutLabel(ymd) {
  const d = daysFromToday(ymd)
  if (d == null) return ''
  if (d === 0) return 'today'
  if (d > 0) return d + ' day' + (d === 1 ? '' : 's') + ' out from today'
  return (-d) + ' day' + (d === -1 ? '' : 's') + ' ago'
}
